import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Dumbbell, LineChart, BellRing, Sparkles, LogOut, Flame, CreditCard, Clock } from "lucide-react";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/workouts", label: "Workouts", icon: Dumbbell, id: "nav-workouts" },
  { to: "/weight", label: "Body Weight", icon: LineChart, id: "nav-weight" },
  { to: "/reminders", label: "Reminders", icon: BellRing, id: "nav-reminders" },
  { to: "/coach", label: "AI Coach", icon: Sparkles, id: "nav-coach", premium: true },
  { to: "/account", label: "Account", icon: CreditCard, id: "nav-account" },
];

const daysLeft = (unix) => unix ? Math.max(0, Math.ceil((unix * 1000 - Date.now()) / 86400000)) : 0;

function TrialBanner({ user }) {
  const navigate = useNavigate();
  if (user?.cancel_at_period_end) {
    return (
      <button onClick={() => navigate("/account")} data-testid="cancel-banner"
        className="w-full bg-[#FF9500]/12 border-b border-[#FF9500]/30 text-[#FF9500] text-sm font-semibold px-6 py-2.5 flex items-center justify-center gap-2 hover:bg-[#FF9500]/20 transition-colors">
        <Clock size={15} /> Subscription ending soon · resume anytime
      </button>
    );
  }
  if (user?.sub_status === "trialing") {
    const d = daysLeft(user?.trial_end);
    return (
      <button onClick={() => navigate("/account")} data-testid="trial-banner"
        className="w-full bg-[#34C759]/12 border-b border-[#34C759]/30 text-[#34C759] text-sm font-semibold px-6 py-2.5 flex items-center justify-center gap-2 hover:bg-[#34C759]/20 transition-colors">
        <Clock size={15} /> {d} {d === 1 ? "day" : "days"} left in your free trial · manage plan
      </button>
    );
  }
  return null;
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-white/10 bg-[#0A0A0A] fixed h-screen z-20">
        <div className="px-6 py-6 border-b border-white/10 flex items-center gap-2">
          <Flame className="text-[#FF3B30]" size={26} strokeWidth={2.5} />
          <span className="font-display font-black text-2xl uppercase tracking-tighter leading-none">
            Reprise<span className="text-[#FF3B30]">Fit</span>
          </span>
        </div>
        <nav className="flex-1 py-6 flex flex-col gap-1 px-3">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm font-semibold uppercase tracking-wide transition-transform duration-150 hover:-translate-y-0.5 ${
                    isActive
                      ? l.premium
                        ? "bg-[#007AFF]/10 text-[#007AFF] border-l-2 border-[#007AFF]"
                        : "bg-[#FF3B30]/10 text-[#FF3B30] border-l-2 border-[#FF3B30]"
                      : "text-white/60 hover:text-white border-l-2 border-transparent"
                  }`
                }
              >
                <Icon size={18} />
                {l.label}
                {l.premium && !user?.has_coach && (
                  <span className="ml-auto text-[9px] bg-[#007AFF]/20 text-[#007AFF] px-1.5 py-0.5 font-bold">PRO</span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <div className="px-4 py-2 mb-2">
            <div className="text-xs text-white/40 uppercase tracking-widest">Signed in</div>
            <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold uppercase tracking-wide text-white/60 hover:text-[#FF3B30] transition-colors"
          >
            <LogOut size={18} /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-black/60 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <Flame className="text-[#FF3B30]" size={22} strokeWidth={2.5} />
          <span className="font-display font-black text-xl uppercase tracking-tighter">RepriseFit</span>
        </div>
        <button onClick={handleLogout} className="text-white/60" data-testid="logout-button-mobile"><LogOut size={20} /></button>
      </div>

      <main className="flex-1 md:ml-64 pt-16 md:pt-0 relative z-10">
        <TrialBanner user={user} />
        {children}
        {/* mobile bottom nav */}
        <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-black/80 backdrop-blur-xl border-t border-white/10 flex justify-around py-2">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink key={l.to} to={l.to} className={({ isActive }) => `flex flex-col items-center gap-1 px-2 py-1 text-[10px] uppercase ${isActive ? "text-[#FF3B30]" : "text-white/50"}`}>
                <Icon size={20} />
              </NavLink>
            );
          })}
        </div>
      </main>
    </div>
  );
}
